import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';
import useUser from '@/hooks/useUser';

import { translateToken } from '../TileActivityLog';

// Static map: backend system source token → literal i18n key (mirrors VERB_KEYS in
// TileActivityLog). The token space is open, so unknown tokens humanize instead.
const SYSTEM_SOURCE_KEYS: Record<string, string> = {
  resolution: 'activityLogs.system_sources.resolution',
  self_reported: 'activityLogs.system_sources.self_reported',
};

export interface ActivityLogLookups {
  /** Actor display name: "You", a translated system label, or the member name. */
  getSubject: (
    sourceId: string | null,
    sourceName: string | null,
    sourceType: string | null
  ) => string;
  getAvatar: (sourceId: string | null, sourceType: string | null) => React.JSX.Element;
  getDesk: (deskId: string | null) => string | undefined;
  getBrain: (brainId: string | null) => string | undefined;
}

/**
 * Resolves the source name / avatar and the desk / brain names for activity-log
 * rows. Builds the id→value lookup maps once, so a feed of N rows shares them
 * instead of each row re-reducing the member/desk/brain lists.
 */
export const useActivityLogLookups = (): ActivityLogLookups => {
  const { t } = useTranslation();
  const { members } = useMembers();
  const { user } = useUser();
  const { desks } = useDesks();
  const { brains } = useBrains();

  const avatarsById = useMemo(
    () =>
      (members ?? []).reduce<Record<string, string | null>>((acc, m) => {
        acc[m.user_id] = m.avatar || null;
        return acc;
      }, {}),
    [members]
  );

  const memberNamesById = useMemo(
    () =>
      (members ?? []).reduce<Record<string, string>>((acc, m) => {
        acc[m.user_id] = m.name;
        return acc;
      }, {}),
    [members]
  );

  const deskNamesById = useMemo(
    () =>
      (desks ?? []).reduce<Record<string, string>>((acc, d) => {
        acc[d.desk_id] = d.name;
        return acc;
      }, {}),
    [desks]
  );

  const brainNamesById = useMemo(
    () =>
      (brains ?? []).reduce<Record<string, string>>((acc, b) => {
        acc[b.brain_id] = b.name;
        return acc;
      }, {}),
    [brains]
  );

  return useMemo(
    () => ({
      getSubject: (sourceId, sourceName, sourceType) => {
        // Resolved before the sourceId guard: a grouped system header carries no source_id.
        if (sourceType === 'system') {
          return sourceName ? translateToken(SYSTEM_SOURCE_KEYS, sourceName, t) : '';
        }
        if (!sourceId) {
          return '';
        }
        if (sourceType === 'user') {
          if (sourceId === user?.user_id) {
            return t('activityLogs.you');
          }
          // Some writers omit source_name — resolve it from the members list.
          return sourceName || memberNamesById[sourceId] || '';
        }
        return sourceName ?? '';
      },
      getAvatar: (sourceId, sourceType) => {
        if (sourceType === 'user' && sourceId) {
          return <Avatar userId={sourceId} size="small" />;
        }
        return <Avatar src={sourceId ? avatarsById[sourceId] : null} size="small" />;
      },
      getDesk: (deskId) => (deskId ? deskNamesById[deskId] : undefined),
      getBrain: (brainId) => (brainId ? brainNamesById[brainId] : undefined),
    }),
    [t, user?.user_id, avatarsById, memberNamesById, deskNamesById, brainNamesById]
  );
};
